import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { api, formatMoney, fileUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  Sandwich, Minus, Plus, ShoppingBag, Search, ImageOff, Info, X, ShieldOff,
} from "lucide-react";
import { toast } from "sonner";

function ProductImage({ producto, className }) {
  if (producto.imagen_path) {
    return (
      <img
        src={fileUrl(producto.imagen_path)}
        alt={producto.nombre}
        loading="lazy"
        className={className}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  if (producto.imagen_url) {
    return (
      <img src={producto.imagen_url} alt={producto.nombre} loading="lazy" className={className} />
    );
  }
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Sandwich className="h-12 w-12 text-[#C85A32]/40" strokeWidth={1.5} />
    </div>
  );
}

function ProductDetailDialog({ producto, open, onClose }) {
  const cart = useCart();
  const current = cart.items.find((x) => x.producto_id === producto?.id);
  const [qty, setQty] = useState(1);

  // Reset qty whenever the dialog opens for a new product
  useEffect(() => {
    if (open) setQty(Math.max(1, current?.cantidad || 1));
  }, [open, producto?.id, current?.cantidad]);

  if (!producto) return null;

  const total = qty * Number(producto.precio || 0);
  const isInCart = !!current;

  const submit = () => {
    if (isInCart) {
      cart.setQty(producto.id, qty);
    } else {
      cart.add(producto, qty);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="p-0 overflow-hidden max-w-lg sm:rounded-2xl bg-white border-[#E5E0D8] gap-0"
        data-testid="cli-product-detail"
      >
        <VisuallyHidden.Root>
          <DialogTitle>{producto.nombre}</DialogTitle>
          <DialogDescription>
            {producto.descripcion || `Detalle de ${producto.nombre}`}
          </DialogDescription>
        </VisuallyHidden.Root>
        <div className="relative bg-[#F4EAE6] aspect-[4/3] sm:aspect-[16/10]">
          <ProductImage
            producto={producto}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {producto.categoria && (
            <div className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-white/95 text-[#595959] backdrop-blur shadow-sm">
              {producto.categoria}
            </div>
          )}
          <DialogClose
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/95 hover:bg-white text-[#595959] hover:text-[#1A1A1A] flex items-center justify-center shadow-sm transition-colors"
            data-testid="cli-product-detail-close"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </div>
        <div className="p-5 sm:p-6 max-h-[60vh] overflow-y-auto">
          <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-[#1A1A1A] leading-tight">
            {producto.nombre}
          </h2>
          {producto.descripcion ? (
            <p className="text-sm text-[#595959] mt-2 whitespace-pre-line">
              {producto.descripcion}
            </p>
          ) : (
            <p className="text-[12px] text-[#8C8C8C] italic mt-2">Sin descripción.</p>
          )}
          <div className="flex items-end justify-between mt-4 pb-4 border-b border-[#E5E0D8]">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Precio unitario</div>
              <div className="font-mono font-bold text-2xl text-[#C85A32]">{formatMoney(producto.precio)}</div>
            </div>
            {isInCart && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#3F6B3C]">En tu carrito</div>
                <div className="font-mono font-bold text-sm text-[#3F6B3C]">{current.cantidad} u</div>
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-wider text-[#8C8C8C] mb-2">Cantidad</div>
            <div className="flex items-stretch gap-2 mb-4">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-12 w-12 rounded-lg border border-[#E5E0D8] text-[#C85A32] hover:bg-[#F4EAE6] flex items-center justify-center disabled:opacity-50"
                disabled={qty <= 1}
                data-testid="cli-detail-minus"
                aria-label="Restar"
              >
                <Minus className="h-5 w-5" />
              </button>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                className="flex-1 h-12 text-center font-mono font-bold text-2xl bg-[#F9F6F0] border border-[#E5E0D8] rounded-lg"
                data-testid="cli-detail-qty"
              />
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                className="h-12 w-12 rounded-lg border border-[#E5E0D8] text-[#C85A32] hover:bg-[#F4EAE6] flex items-center justify-center"
                data-testid="cli-detail-plus"
                aria-label="Sumar"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center justify-between text-sm mb-4">
              <span className="text-[#595959]">Total</span>
              <span className="font-mono font-bold text-lg text-[#1A1A1A]" data-testid="cli-detail-total">
                {formatMoney(total)}
              </span>
            </div>
            <Button
              onClick={submit}
              className="w-full bg-[#C85A32] hover:bg-[#A64A29] text-white h-12 gap-2 font-semibold"
              data-testid="cli-detail-confirm"
            >
              <ShoppingBag className="h-4 w-4" />
              {isInCart ? "Actualizar cantidad" : "Agregar al carrito"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductCard({ producto, onOpen, suspended }) {
  const cart = useCart();
  const current = cart.items.find((x) => x.producto_id === producto.id);
  const qty = current?.cantidad || 0;

  const blockedToast = () => {
    toast.error("Cuenta suspendida. Hable con el vendedor para regularizar su situación.");
  };

  return (
    <Card
      className={`overflow-hidden border-[#E5E0D8] rounded-xl bg-white flex flex-col group transition-all ${
        suspended
          ? "opacity-60 cursor-not-allowed"
          : "hover:shadow-md hover:-translate-y-0.5 hover:border-[#C85A32]/40 cursor-pointer"
      }`}
      data-testid={`cli-prod-${producto.id}`}
      onClick={() => onOpen(producto)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(producto); } }}
      aria-disabled={suspended ? "true" : "false"}
    >
      <div className="aspect-square bg-[#F4EAE6] relative overflow-hidden">
        <ProductImage
          producto={producto}
          className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
        />
        <div className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-white/90 text-[#595959] backdrop-blur">
          {producto.categoria}
        </div>
        {/* "Ver detalle" hint — visible on hover desktop, always faint on mobile */}
        <div className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#1A1A1A]/70 text-white opacity-0 group-hover:opacity-100 sm:opacity-0 transition-opacity">
          <Info className="h-2.5 w-2.5" /> Ver
        </div>
      </div>
      <div className="p-2.5 sm:p-3 flex-1 flex flex-col">
        <div className="font-display font-semibold text-[13px] sm:text-[14px] leading-tight text-[#1A1A1A] line-clamp-2 min-h-[2.2em]">
          {producto.nombre}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-1">
          <div className="font-mono font-bold text-[15px] sm:text-base text-[#C85A32]">
            {formatMoney(producto.precio)}
          </div>
          <span className="text-[10px] text-[#8C8C8C] hidden sm:inline-flex items-center gap-0.5 group-hover:text-[#C85A32]">
            <Info className="h-2.5 w-2.5" /> detalle
          </span>
        </div>
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          {suspended ? (
            <Button
              type="button"
              disabled
              className="w-full h-9 bg-[#E5E0D8] text-[#8C8C8C] gap-1.5 rounded-lg text-[13px] cursor-not-allowed hover:bg-[#E5E0D8]"
              onClick={blockedToast}
              data-testid={`cli-prod-${producto.id}-blocked`}
            >
              <ShieldOff className="h-3.5 w-3.5" /> Suspendida
            </Button>
          ) : qty === 0 ? (
            <Button
              onClick={() => cart.add(producto, 1)}
              className="w-full h-9 bg-[#C85A32] hover:bg-[#A64A29] text-white gap-1.5 rounded-lg text-[13px]"
              data-testid={`cli-prod-${producto.id}-add`}
            >
              <Plus className="h-3.5 w-3.5" /> Agregar
            </Button>
          ) : (
            <div className="flex items-center h-9 bg-[#F9F6F0] rounded-lg overflow-hidden border border-[#E5E0D8]">
              <button
                type="button"
                onClick={() => cart.setQty(producto.id, qty - 1)}
                className="flex-1 h-full flex items-center justify-center text-[#C85A32] hover:bg-[#F4EAE6]"
                aria-label="Restar"
                data-testid={`cli-prod-${producto.id}-minus`}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <div className="flex-1 text-center font-mono font-bold text-sm text-[#1A1A1A]" data-testid={`cli-prod-${producto.id}-qty`}>
                {qty}
              </div>
              <button
                type="button"
                onClick={() => cart.add(producto, 1)}
                className="flex-1 h-full flex items-center justify-center text-[#C85A32] hover:bg-[#F4EAE6]"
                aria-label="Sumar"
                data-testid={`cli-prod-${producto.id}-plus`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function ClienteCatalogo() {
  const navigate = useNavigate();
  const cart = useCart();
  const outlet = useOutletContext() || {};
  const suspended = !!outlet.suspended;
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("Todos");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/productos").then((r) => setProductos(r.data)).finally(() => setLoading(false));
  }, []);

  const handleOpen = (producto) => {
    if (suspended) {
      toast.error("Cuenta suspendida. Hable con el vendedor para regularizar su situación.");
      return;
    }
    setDetail(producto);
  };

  const cats = useMemo(() => {
    const s = new Set(["Todos"]);
    productos.forEach((p) => s.add(p.categoria || "Otros"));
    return Array.from(s);
  }, [productos]);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return productos.filter((p) => {
      if (cat !== "Todos" && (p.categoria || "Otros") !== cat) return false;
      if (qn && !p.nombre.toLowerCase().includes(qn) && !(p.descripcion || "").toLowerCase().includes(qn)) return false;
      return true;
    });
  }, [productos, cat, q]);

  return (
    <div>
      <div className="mb-6 sm:mb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-2">Catálogo</div>
        <div className="flex items-end justify-between gap-3">
          <h1 className="font-display font-bold text-3xl sm:text-5xl tracking-tight">Elegí tus sánguches</h1>
          {cart.count > 0 && (
            <Link to="/cliente/carrito" className="hidden sm:inline-flex">
              <Button className="bg-[#1A1A1A] hover:bg-[#000] text-white h-10 gap-2 rounded-full px-5" data-testid="cli-go-cart">
                <ShoppingBag className="h-4 w-4" /> Carrito · {cart.count}
              </Button>
            </Link>
          )}
        </div>
        <p className="text-[#595959] mt-2 text-sm sm:text-base max-w-xl">
          Sumá productos al carrito y enviá tu pedido. Te avisamos cuando esté listo.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C8C8C]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar producto..."
            className="pl-9 h-11 bg-white"
            data-testid="cli-search"
          />
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="flex gap-1.5 px-4 sm:px-0">
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                data-testid={`cli-cat-${c}`}
                className={`shrink-0 px-3.5 py-2 rounded-full text-[13px] font-medium border transition-colors ${
                  cat === c
                    ? "bg-[#1A1A1A] border-[#1A1A1A] text-[#F9F6F0]"
                    : "bg-white border-[#E5E0D8] text-[#595959] hover:border-[#1A1A1A]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Card key={`skeleton-${i}`} className="overflow-hidden border-[#E5E0D8] rounded-xl bg-white">
              <div className="aspect-square bg-[#F4EAE6] animate-pulse" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-[#F4EAE6] rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-[#F4EAE6] rounded animate-pulse" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-[#E5E0D8] rounded-xl p-10 text-center bg-white">
          <ImageOff className="h-10 w-10 mx-auto text-[#C85A32] mb-3 opacity-60" />
          <div className="font-display font-semibold text-lg">Sin productos en esta categoría</div>
          <p className="text-sm text-[#595959] mt-1">Probá con otra categoría o limpiá la búsqueda.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 pb-32 sm:pb-28"
          data-testid="cli-grid"
        >
          {filtered.map((p) => <ProductCard key={p.id} producto={p} onOpen={handleOpen} suspended={suspended} />)}
        </div>
      )}

      <ProductDetailDialog producto={detail} open={!!detail} onClose={() => setDetail(null)} />

      {/* Floating cart button — mobile + desktop, warm brand color */}
      {cart.count > 0 && (
        <button
          type="button"
          onClick={() => navigate("/cliente/carrito")}
          className="fixed z-30 flex items-center gap-3 rounded-full shadow-xl ring-1 ring-black/5
                     bg-[#C85A32] hover:bg-[#A64A29] active:bg-[#923D22] text-white
                     transition-all hover:shadow-2xl hover:-translate-y-0.5
                     bottom-20 left-1/2 -translate-x-1/2 px-5 py-3
                     sm:bottom-6 sm:right-6 sm:left-auto sm:translate-x-0 sm:px-6 sm:py-3.5"
          data-testid="cli-floating-cart"
        >
          <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="font-semibold text-sm sm:text-[15px]">
            Ver carrito · {cart.count}
          </span>
          <span className="font-mono font-bold text-sm sm:text-[15px] pl-2 ml-1 border-l border-white/30">
            {formatMoney(cart.total)}
          </span>
        </button>
      )}
    </div>
  );
}
